import styled from "styled-components";

import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { PageBreak } from "app/components/layout/PageBreak";

import { TopAdvertisersPublishersTable } from "../../common/advertisersPublishers/TopAdvertisersPublishers";
import { useDisplayAdvertiserPublisherDataAndParams } from "../../common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { ActivityBreakdownCompare } from "../../common/overview/compare/ActivityBreakdownCompare";
import { AdPlacementsCompare } from "../../common/overview/compare/AdPlacementsCompare";
import { DeviceBreakdownCompare } from "../../common/overview/compare/DeviceBreakdownCompare";
import { FormatBreakdownCompare } from "../../common/overview/compare/FormatBreakdownCompare";
import { PerformanceOverTimeCompareGraph } from "../../common/overview/compare/PerformanceOverTimeCompareGraph";
import { PerformanceWidgetCompare } from "../../common/overview/compare/PerformanceWidgetCompare";
import { CountryMapWidget } from "../../common/overview/CountryMapWidget";
import { PerformanceOverTimeGraph } from "../../common/overview/PerformanceOverTimeGraph";
import { PerformanceWidget } from "../../common/overview/PerformanceWidget";
import { RecentAdsGallery } from "../../common/overview/RecentAdsGallery";
import { BlurContainer, MEDIUM_SCREEN_BREAKPOINT } from "../../common/overview/StyledComponents";
import { VerificationWidget } from "../../common/overview/verification/VerificationWidget";
import { hasVerificationWidgetAccess } from "../../common/utils/utils";

import { PreviewPerformanceWidget } from "./components/preview-performance-widget/PreviewPerformanceWidget";
import { PreviewPerformanceWidgetCompare } from "./components/PreviewPerformanceWidgetCompare";
import { RecentCampaignsTable } from "./components/RecentCampaignsTable";

const ThreeColumnContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;

    @media (max-width: ${MEDIUM_SCREEN_BREAKPOINT}) {
        grid-template-columns: repeat(2, 1fr);
        row-gap: 0;
    }
`;

const GridItemWrapper = styled.div`
    min-width: 0;
`;

const AdPlacementWrapper = styled(GridItemWrapper)`
    @media (max-width: ${MEDIUM_SCREEN_BREAKPOINT}) {
        grid-column: span 2;
    }
`;

const MarginBottomWrapper = styled.div`
    margin-bottom: 24px;
`;

const getSingleKeyPerformanceWidget = (isPreview: boolean, hideCountryMap: boolean) => {
    const canViewVerificationWidget = hasVerificationWidgetAccess();
    if (isPreview) {
        return (
            <>
                <PreviewPerformanceWidget performanceOverTimeGraphComponent={<PerformanceOverTimeGraph isPreview />} />
                {!hideCountryMap ? (
                    <MarginBottomWrapper>
                        <CountryMapWidget isPreview />
                    </MarginBottomWrapper>
                ) : null}
                <PageBreak applyPageBreak />
            </>
        );
    }

    return (
        <>
            <PerformanceWidget performanceOverTimeGraphComponent={<PerformanceOverTimeGraph />} />

            {canViewVerificationWidget ? (
                <MarginBottomWrapper>
                    <VerificationWidget />
                </MarginBottomWrapper>
            ) : null}
            <MarginBottomWrapper>
                <CountryMapWidget />
            </MarginBottomWrapper>
            <PageBreak applyPageBreak />
        </>
    );
};

const getMultiKeyPerformanceWidget = (isPreview: boolean) => {
    const canViewVerificationWidget = hasVerificationWidgetAccess();
    return (
        <>
            {isPreview ? <PreviewPerformanceWidgetCompare /> : <PerformanceWidgetCompare />}
            <ActivityBreakdownCompare />
            <PerformanceOverTimeCompareGraph isPreview={isPreview} />
            {isPreview ? null : (
                <ThreeColumnContainer>
                    <GridItemWrapper>
                        <DeviceBreakdownCompare />
                    </GridItemWrapper>
                    <GridItemWrapper>
                        <FormatBreakdownCompare />
                    </GridItemWrapper>
                    <AdPlacementWrapper>
                        <AdPlacementsCompare />
                    </AdPlacementWrapper>
                </ThreeColumnContainer>
            )}
            {canViewVerificationWidget && !isPreview ? (
                <MarginBottomWrapper>
                    <VerificationWidget />
                </MarginBottomWrapper>
            ) : null}
        </>
    );
};

export const DisplayAdvertisingOverviewPage = ({ isPreview = false }: { isPreview?: boolean }) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const domainKeys = (params.key ?? "").split(",");
    const isCompareMode = domainKeys.length > 1;
    const hideCountryMap = isPreview && isAvailable(swSettings.components.AdIntMaster);

    return (
        <BlurContainer $active={isPreview}>
            {isCompareMode
                ? getMultiKeyPerformanceWidget(isPreview)
                : getSingleKeyPerformanceWidget(isPreview, hideCountryMap)}

            <RecentCampaignsTable isPreview={isPreview} />
            <PageBreak applyPageBreak />
            <RecentAdsGallery
                isPreview={isPreview}
                isOverviewContext
            />
            <PageBreak applyPageBreak />
            <TopAdvertisersPublishersTable isPreview={isPreview} />
        </BlurContainer>
    );
};
